import { useState, useMemo, useRef, useEffect } from 'react';
import { toast } from 'sonner';
import posthog from '../../lib/posthog';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Sidebar } from '../../components/ui/sidebar';
import {
  AlertTriangle,
  BarChart2,
  Bot,
  ChevronDown,
  ClipboardList,
  Download,
  ExternalLink,
  Loader2,
  PlayCircle,
  RotateCcw,
  Users,
} from 'lucide-react';
import { useSimulations } from '../../contexts/SimulationContext';
import { useEvaluators } from '../../contexts/EvaluatorContext';
import { format } from 'date-fns';
import { Button } from '../../components/ui/button/button';
import { evaluateDialogue } from '../../lib/api';
import { QuotaExceededError } from '../../lib/custom-errors';
import { EvaluationResult, Simulation } from '../../lib/types';
import { Progress } from '../../components/ui/progress';
import { Tabs, TabsContent } from '../../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useAuthStore } from '../../stores/authStore';
import LoadingSpinner from '../../components/ui/loading-spinner';
import {
  formatEvaluationScore,
  getEvaluationScoreTitle,
  getEvaluationScoreColor,
  isNotApplicableScore,
} from '../../lib/utils';
import { isConnectedAgent } from '../../lib/edge-functions';
import {
  isEvaluationBlocked,
  isSimulationFailed,
  isSimulationGenerating,
} from '../../lib/utils/simulation-status';
import EvaluationResultCard from '../../components/ui/evaluation-result-card/evaluation-result-card';
import EvaluationConversation from '../../components/ui/evaluation-conversation/evaluation-conversation';
import { useGlobal } from '../../contexts/GlobalProvider';
import { EntityType } from '../../lib/enums';
import {
  buildAgentSimulationsCsv,
  buildSimulationCsv,
  downloadCsv,
} from './csvExport';
import { PageHeader } from '../../components/ui/page-header/page-header';
import { ImprovementSuggestions } from '../../components/ui/improvement-suggestions/improvement-suggestions';
import { DismissibleTooltip } from '../../components/ui/tooltip';
import evaluationsData from '../../data/evaluations/data.json';
import { Link, useSearchParams } from 'react-router-dom';
import { useTutorialStore } from '../../stores/tutorialStore';

const CLEAR_SCORES_TOOLTIP_DISMISSED_KEY = 'clearScoresTooltipDismissed';

export const Evaluations = (): JSX.Element => {
  const { user } = useAuthStore();
  const { hiddenExamples } = useGlobal();
  const { isActive: isOnboardingActive, markMilestoneComplete } =
    useTutorialStore();
  const [searchParams] = useSearchParams();
  const focusedSimulationId = searchParams.get('simulationId');
  const evaluationQueueRef = useRef<(() => Promise<void>)[]>([]);
  const isProcessingQueueRef = useRef(false);
  const focusedSimulationSelectionRef = useRef<string | null>(null);
  const focusedSimulationExpansionRef = useRef<string | null>(null);
  const focusedSimulationScrollRef = useRef<string | null>(null);

  const { simulations, updateSimulation, fetchSimulation, loading, error } =
    useSimulations();
  const { evaluators } = useEvaluators();

  // Filter out hidden evaluators
  const visibleEvaluators = useMemo(() => {
    const isHidden = hiddenExamples.includes(EntityType.Evaluators);
    return isHidden ? evaluators.filter((e) => !e.is_example) : evaluators;
  }, [evaluators, hiddenExamples]);

  const [expandedEvaluatorIdBySimulation, setExpandedEvaluatorIdBySimulation] =
    useState<Record<string, string | null>>({});
  const [evaluationResults, setEvaluationResults] = useState<
    Record<string, Record<string, EvaluationResult>>
  >({});
  const [runningAllEvaluations, setRunningAllEvaluations] = useState<
    Record<string, boolean>
  >({});
  const [selectedAgentId, setSelectedAgentId] = useState<string | null>(null);

  const enqueueEvaluation = (request: () => Promise<void>) => {
    evaluationQueueRef.current.push(request);

    if (!isProcessingQueueRef.current) {
      processEvaluationQueue();
    }
  };

  const processEvaluationQueue = async () => {
    isProcessingQueueRef.current = true;

    while (evaluationQueueRef.current.length > 0) {
      const job = evaluationQueueRef.current.shift();
      if (job) {
        try {
          await job();
        } catch (error) {
          if (error instanceof QuotaExceededError) break;
          // Error already handled inside the job; keep processing the queue.
        }
      }
    }

    isProcessingQueueRef.current = false;
  };

  const simulationsByAgent = useMemo(() => {
    const grouped = simulations.reduce(
      (acc, simulation) => {
        const agentId = simulation.agent_id;
        if (!acc[agentId]) {
          acc[agentId] = [];
        }
        acc[agentId].push(simulation);
        return acc;
      },
      {} as Record<string, typeof simulations>,
    );

    Object.keys(grouped).forEach((agentId) => {
      grouped[agentId].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );
    });

    return grouped;
  }, [simulations]);

  const agentIds = useMemo(
    () => Object.keys(simulationsByAgent),
    [simulationsByAgent],
  );

  useEffect(() => {
    if (agentIds.length === 0) {
      setSelectedAgentId(null);
      return;
    }

    if (!selectedAgentId || !simulationsByAgent[selectedAgentId]) {
      setSelectedAgentId(agentIds[0]);
    }
  }, [agentIds, selectedAgentId, simulationsByAgent]);

  useEffect(() => {
    if (!focusedSimulationId) {
      focusedSimulationSelectionRef.current = null;
      focusedSimulationExpansionRef.current = null;
      focusedSimulationScrollRef.current = null;
      return;
    }

    const focusedSimulation = simulations.find(
      (simulation) => simulation.id === focusedSimulationId,
    );
    if (!focusedSimulation) return;

    markMilestoneComplete('evaluationOpened');

    if (focusedSimulationSelectionRef.current !== focusedSimulation.id) {
      focusedSimulationSelectionRef.current = focusedSimulation.id;
      setSelectedAgentId(focusedSimulation.agent_id);
    }

    const firstCompletedEvaluator = visibleEvaluators.find(
      (evaluator) => focusedSimulation.evaluations?.[evaluator.id],
    );
    const isFocusedSimulationRunningAll =
      runningAllEvaluations[focusedSimulation.id];

    if (firstCompletedEvaluator && !isFocusedSimulationRunningAll) {
      markMilestoneComplete('evaluationsRun');
    }
    if (
      firstCompletedEvaluator &&
      !isOnboardingActive &&
      focusedSimulationExpansionRef.current !== focusedSimulation.id
    ) {
      focusedSimulationExpansionRef.current = focusedSimulation.id;
      setExpandedEvaluatorIdBySimulation((prev) => ({
        ...prev,
        [focusedSimulation.id]: firstCompletedEvaluator.id,
      }));
    }

    if (focusedSimulationScrollRef.current === focusedSimulation.id) return;
    focusedSimulationScrollRef.current = focusedSimulation.id;
    window.setTimeout(() => {
      document
        .getElementById(`evaluation-simulation-${focusedSimulation.id}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
  }, [
    focusedSimulationId,
    isOnboardingActive,
    markMilestoneComplete,
    runningAllEvaluations,
    simulations,
    visibleEvaluators,
  ]);

  const toggleEvaluatorDetail = (simulationId: string, evaluatorId: string) => {
    if (expandedEvaluatorIdBySimulation[simulationId] !== evaluatorId) {
      markMilestoneComplete('evaluationScoreOpened');
    }
    setExpandedEvaluatorIdBySimulation((prev) => ({
      ...prev,
      [simulationId]: prev[simulationId] === evaluatorId ? null : evaluatorId,
    }));
  };

  const getAverageScore = (
    evaluations: Record<string, EvaluationResult>,
  ): number | null => {
    const visibleEntries = Object.entries(evaluations).filter(([evaluatorId]) =>
      visibleEvaluators.some((e) => e.id === evaluatorId),
    );
    if (visibleEntries.length === 0) return null;

    const scores = visibleEntries
      .map(([, evaluation]) => evaluation.score)
      .filter((score) => !isNotApplicableScore(score)) as number[];
    if (scores.length === 0) return null;
    return scores.reduce((a, b) => a + b, 0) / scores.length;
  };

  const getCompletedEvaluations = (
    evaluations: Record<string, EvaluationResult>,
  ) => {
    // Only count evaluations for visible evaluators
    return Object.keys(evaluations).filter((evaluatorId) =>
      visibleEvaluators.some((e) => e.id === evaluatorId),
    ).length;
  };

  const exportReport = (simulation: Simulation) => {
    const csvContent = buildSimulationCsv(simulation, visibleEvaluators);

    downloadCsv(
      csvContent,
      `simulation-report-${format(
        new Date(simulation.created_at),
        'yyyy-MM-dd-HH-mm',
      )}.csv`,
    );

    posthog.capture('evaluation_report_exported', {
      simulation_id: simulation.id,
      agent_id: simulation.agent_id,
      export_type: 'single',
      workspace_id: user?.currentWorkspace?.workspace_id,
    });
    markMilestoneComplete('evaluationExported');
  };

  const exportAllReports = (agentId: string) => {
    const agentSimulations = simulationsByAgent[agentId] || [];
    const agent = agentSimulations[0]?.agent;

    const csvContent = buildAgentSimulationsCsv(
      agent?.name,
      agentSimulations,
      visibleEvaluators,
    );

    downloadCsv(
      csvContent,
      `all-evaluations-${agent?.name || 'unknown'}-${format(
        new Date(),
        'yyyy-MM-dd-HH-mm',
      )}.csv`,
    );

    posthog.capture('evaluation_report_exported', {
      agent_id: agentId,
      simulation_count: agentSimulations.length,
      export_type: 'all',
      workspace_id: user?.currentWorkspace?.workspace_id,
    });
    markMilestoneComplete('evaluationExported');
  };

  const runEvaluation = async (simulationId: string, evaluatorId: string) => {
    const simulation = simulations.find((s) => s.id === simulationId);
    const evaluator = evaluators.find((e) => e.id === evaluatorId);

    if (!simulation || !evaluator) return;

    setEvaluationResults((prev) => ({
      ...prev,
      [simulationId]: {
        ...prev[simulationId],
        [evaluatorId]: { score: 0, feedback: '', status: 'loading' },
      },
    }));

    const evaluationRequest = async () => {
      try {
        const simulationRecord = await fetchSimulation(simulationId);

        if (!simulationRecord) {
          throw new Error('Failed to evaluate: simulation not found');
        }

        const clearPending = () =>
          setEvaluationResults((prev) => {
            const simResults = { ...prev[simulationId] };
            delete simResults[evaluatorId];
            return { ...prev, [simulationId]: simResults };
          });
        if (isEvaluationBlocked(simulationRecord)) {
          clearPending();
          console.warn(
            '[evaluate] Skipped: simulation was generating when the queued job ran.',
          );
          return;
        }

        const result = await evaluateDialogue(
          simulationRecord.messages,
          evaluator.statement,
          user?.currentWorkspace?.workspace_id,
        );

        const latestRecord = await fetchSimulation(simulationId);
        if (
          !latestRecord ||
          isEvaluationBlocked(latestRecord) ||
          latestRecord.messages.length !== simulationRecord.messages.length
        ) {
          clearPending();
          console.warn(
            '[evaluate] Discarded result: simulation changed while evaluating.',
          );
          return;
        }

        const existingEvaluations = latestRecord.evaluations;

        const previousResult = existingEvaluations?.[evaluatorId];
        const statementUnchanged =
          previousResult?.evaluator_statement != null
            ? previousResult.evaluator_statement === evaluator.statement
            : previousResult?.evaluated_at != null
              ? new Date(evaluator.updated_at) <=
                new Date(previousResult.evaluated_at)
              : false;
        const prevIsNA =
          previousResult != null
            ? isNotApplicableScore(previousResult.score)
            : null;
        const newIsNA = isNotApplicableScore(result.score);
        const keepPreviousResult =
          previousResult != null && statementUnchanged && prevIsNA !== newIsNA;
        if (keepPreviousResult) {
          console.warn(
            '[evaluate] Kept previous result: N/A ↔ score flip suppressed.',
          );
          posthog.capture('evaluation_previous_result_kept');
        }
        const finalResult = keepPreviousResult
          ? previousResult
          : {
              ...result,
              evaluator_statement: evaluator.statement,
              scored_message_count: simulationRecord.messages.length,
            };

        const updatedEvaluations = {
          ...existingEvaluations,
          [evaluatorId]: finalResult,
        };

        const persisted = await updateSimulation(
          simulationId,
          { evaluations: updatedEvaluations },
          { requireUpdatedAt: simulationRecord.updated_at },
        );
        if (!persisted) {
          clearPending();
          console.warn(
            '[evaluate] Discarded result: simulation changed at persist time.',
          );
          return;
        }

        posthog.capture('evaluation_run', {
          simulation_id: simulationId,
          evaluator_id: evaluatorId,
          workspace_id: user?.currentWorkspace?.workspace_id,
        });

        setEvaluationResults((prev) => ({
          ...prev,
          [simulationId]: {
            ...prev[simulationId],
            [evaluatorId]: { ...finalResult, status: 'complete' },
          },
        }));

        return finalResult;
      } catch (error) {
        posthog.captureException(error);
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to evaluate';
        setEvaluationResults((prev) => ({
          ...prev,
          [simulationId]: {
            ...prev[simulationId],
            [evaluatorId]: {
              score: 0,
              feedback: '',
              status: 'error',
              error: errorMessage,
            },
          },
        }));
        toast.error(errorMessage);
        throw error;
      }
    };

    enqueueEvaluation(evaluationRequest);
  };

  const clearEvaluationScores = async (simulationId: string) => {
    try {
      await updateSimulation(simulationId, { evaluations: {} });
      setEvaluationResults((prev) => {
        const next = { ...prev };
        delete next[simulationId];
        return next;
      });
      setExpandedEvaluatorIdBySimulation((prev) => ({
        ...prev,
        [simulationId]: null,
      }));
      posthog.capture('evaluation_scores_cleared', {
        simulation_id: simulationId,
        workspace_id: user?.currentWorkspace?.workspace_id,
      });
    } catch (error) {
      posthog.captureException(error);
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to clear evaluation scores',
      );
    }
  };

  const runAllEvaluations = async (simulationId: string) => {
    const simulation = simulations.find((s) => s.id === simulationId);
    if (!simulation) return;

    const simulationEvaluations = simulation.evaluations ?? {};
    setRunningAllEvaluations((prev) => ({ ...prev, [simulationId]: true }));

    try {
      const pendingEvaluators = visibleEvaluators.filter(
        (evaluator) => !simulationEvaluations[evaluator.id],
      );

      posthog.capture('evaluation_run_all', {
        simulation_id: simulationId,
        workspace_id: user?.currentWorkspace?.workspace_id,
        run_all_evaluations_count: pendingEvaluators.length,
      });

      let updatedEvaluations = { ...simulationEvaluations };

      for (const evaluator of pendingEvaluators) {
        try {
          const simulationRecord = await fetchSimulation(simulationId);
          if (!simulationRecord || isEvaluationBlocked(simulationRecord)) {
            break;
          }

          const result = await evaluateDialogue(
            simulationRecord.messages,
            evaluator.statement,
            user?.currentWorkspace?.workspace_id,
          );

          const latestRecord = await fetchSimulation(simulationId);
          if (
            !latestRecord ||
            isEvaluationBlocked(latestRecord) ||
            latestRecord.messages.length !== simulationRecord.messages.length
          ) {
            break;
          }

          updatedEvaluations = {
            ...updatedEvaluations,
            [evaluator.id]: {
              ...result,
              evaluator_statement: evaluator.statement,
              scored_message_count: simulationRecord.messages.length,
            },
          };

          const persisted = await updateSimulation(
            simulationId,
            { evaluations: updatedEvaluations },
            { requireUpdatedAt: simulationRecord.updated_at },
          );
          if (!persisted) {
            break;
          }

          setEvaluationResults((prev) => ({
            ...prev,
            [simulationId]: {
              ...prev[simulationId],
              [evaluator.id]: { ...result, status: 'complete' },
            },
          }));
        } catch (error) {
          posthog.captureException(error);
          console.error(`Error evaluating with ${evaluator.name}:`, error);
          if (error instanceof QuotaExceededError) break;
          toast.error(
            error instanceof Error ? error.message : 'Failed to evaluate',
          );
        }
      }
    } finally {
      setRunningAllEvaluations((prev) => ({ ...prev, [simulationId]: false }));
      markMilestoneComplete('evaluationsRun');
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-prompt-safecomwhite">
        <div className="text-prompt-safecomvulcan">Loading evaluations...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-prompt-safecomwhite">
        <div className="text-red-600">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-prompt-safecomwhite">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="flex h-full w-full flex-col">
          <PageHeader
            title="Evaluations"
            icon={
              <BarChart2 className="h-6 w-6 text-prompt-safecomoutrageous-orange" />
            }
          />

          <div className="p-6">
            <Card
              className="rounded-[10px] border border-solid border-[#00000020] bg-prompt-safecomfantasy shadow-[0px_2px_4px_#0000001a]"
              data-onboarding="evaluations-list"
            >
              <CardHeader>
                <CardTitle className="font-prompt-safe-com-semantic-heading-5 text-prompt-safecomvulcan">
                  Simulated conversations
                </CardTitle>
              </CardHeader>
              <CardContent>
                {agentIds.length > 0 ? (
                  <Tabs
                    value={selectedAgentId ?? agentIds[0]}
                    onValueChange={setSelectedAgentId}
                  >
                    <div className="mb-4 flex gap-3 max-md:flex-col max-md:gap-2 md:items-end md:justify-between">
                      <div className="flex flex-col gap-2">
                        <label className="text-sm font-medium text-prompt-safecomvulcan">
                          Selected agent:
                        </label>
                        <Select
                          value={selectedAgentId ?? agentIds[0]}
                          onValueChange={setSelectedAgentId}
                        >
                          <SelectTrigger className="bg-prompt-safecomwhite md:w-64">
                            <SelectValue placeholder="Select an agent" />
                          </SelectTrigger>
                          <SelectContent>
                            {agentIds.map((agentId) => {
                              const agentSimulations =
                                simulationsByAgent[agentId];
                              return (
                                <SelectItem key={agentId} value={agentId}>
                                  {agentSimulations[0]?.agent?.name ||
                                    'Unknown Agent'}{' '}
                                  ({agentSimulations.length})
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                      {selectedAgentId && (
                        <Button
                          variant="outline"
                          onClick={() => exportAllReports(selectedAgentId)}
                          className="flex items-center gap-2"
                        >
                          <Download className="h-4 w-4" />
                          Export all reports
                        </Button>
                      )}
                    </div>
                    {Object.entries(simulationsByAgent).map(
                      ([agentId, agentSimulations]) => (
                        <TabsContent
                          key={agentId}
                          value={agentId}
                          className="space-y-4"
                        >
                          {agentSimulations.map((simulation) => {
                            const isRunningAll =
                              runningAllEvaluations[simulation.id];
                            const isGenerating =
                              isSimulationGenerating(simulation);
                            const isFailed = isSimulationFailed(simulation);
                            const isBlocked = isGenerating || isFailed;
                            const simulationEvaluations =
                              simulation.evaluations ?? {};
                            const averageScore = getAverageScore(
                              simulationEvaluations,
                            );
                            const completedEvaluations =
                              getCompletedEvaluations(simulationEvaluations);
                            const totalEvaluators = visibleEvaluators.length;
                            const hasAllEvaluations =
                              completedEvaluations === totalEvaluators;
                            const hasPendingEvaluation = Object.values(
                              evaluationResults[simulation.id] ?? {},
                            ).some((r) => r?.status === 'loading');
                            const expandedEvaluatorId =
                              expandedEvaluatorIdBySimulation[simulation.id] ??
                              null;
                            const expandedEvaluator = expandedEvaluatorId
                              ? visibleEvaluators.find(
                                  (e) => e.id === expandedEvaluatorId,
                                )
                              : undefined;
                            const firstCompletedEvaluatorId =
                              visibleEvaluators.find(
                                (evaluator) =>
                                  simulationEvaluations[evaluator.id],
                              )?.id;
                            const personaName =
                              simulation.persona?.name || 'Unknown Persona';
                            const agentName =
                              simulation.agent?.name || 'Unknown Agent';
                            const isFocusedSimulation =
                              simulation.id === focusedSimulationId;

                            return (
                              <div
                                key={simulation.id}
                                id={`evaluation-simulation-${simulation.id}`}
                                className={`rounded-lg border bg-prompt-safecomwhite p-4 ${
                                  isFocusedSimulation
                                    ? 'border-prompt-safecomoutrageous-orange ring-2 ring-prompt-safecomoutrageous-orange ring-offset-2 ring-offset-prompt-safecomfantasy'
                                    : 'border-[#00000020]'
                                }`}
                              >
                                <div className="mb-4 flex items-start justify-between max-md:flex-col max-md:gap-2">
                                  <div>
                                    <h3 className="font-semibold text-prompt-safecomvulcan">
                                      {personaName} – {agentName}
                                    </h3>
                                    <p className="text-sm text-prompt-safecomabbey">
                                      {format(
                                        new Date(simulation.created_at),
                                        'PPpp',
                                      )}
                                      {simulation.agent_version &&
                                        ` · Agent v${simulation.agent_version}`}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-2 max-md:w-full max-md:justify-between">
                                    <div className="flex flex-col gap-2 lg:flex-row">
                                      {completedEvaluations > 0 && (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          data-onboarding={
                                            isFocusedSimulation
                                              ? 'evaluation-export-report'
                                              : undefined
                                          }
                                          onClick={() =>
                                            exportReport(simulation)
                                          }
                                          className="flex items-center gap-2"
                                        >
                                          <Download className="h-4 w-4" />
                                          Export report
                                        </Button>
                                      )}
                                      {!hasAllEvaluations && (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          data-onboarding={
                                            isFocusedSimulation
                                              ? 'run-all-evaluations'
                                              : undefined
                                          }
                                          onClick={() =>
                                            runAllEvaluations(simulation.id)
                                          }
                                          disabled={
                                            isRunningAll ||
                                            hasPendingEvaluation ||
                                            isBlocked
                                          }
                                          className="flex items-center gap-2"
                                        >
                                          {isRunningAll ? (
                                            <LoadingSpinner strokeColor="black" />
                                          ) : (
                                            <PlayCircle className="h-4 w-4" />
                                          )}
                                          {isRunningAll
                                            ? 'Running...'
                                            : 'Run all evaluations'}
                                        </Button>
                                      )}
                                      {completedEvaluations > 0 && (
                                        <DismissibleTooltip
                                          storageKey={
                                            CLEAR_SCORES_TOOLTIP_DISMISSED_KEY
                                          }
                                          content={
                                            evaluationsData.clearScoresButton
                                              .tooltip
                                          }
                                        >
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() =>
                                              clearEvaluationScores(
                                                simulation.id,
                                              )
                                            }
                                            disabled={
                                              isRunningAll ||
                                              hasPendingEvaluation
                                            }
                                            className="flex items-center gap-2"
                                          >
                                            <RotateCcw className="h-4 w-4" />
                                            Clear scores
                                          </Button>
                                        </DismissibleTooltip>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {isFailed && (
                                  <div className="mb-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
                                    <span>
                                      {simulation.failure_reason ??
                                        'This simulation stopped early.'}{' '}
                                      A half conversation can't be scored.
                                      Continue it in Conversation Simulation to
                                      finish it.
                                    </span>
                                  </div>
                                )}

                                {isGenerating && (
                                  <div className="mb-4 flex items-center gap-2 rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Simulation in progress — evaluation
                                    unavailable
                                  </div>
                                )}

                                <div className="mb-4">
                                  <div className="mb-2 flex items-center justify-between">
                                    <span className="text-sm text-prompt-safecomabbey">
                                      Overall score ({completedEvaluations} of{' '}
                                      {totalEvaluators} evaluations)
                                    </span>
                                    <span
                                      className={`rounded px-2 py-1 text-sm font-medium ${
                                        completedEvaluations === 0
                                          ? 'bg-gray-100 text-gray-600'
                                          : averageScore === null
                                            ? getEvaluationScoreColor('N/A')
                                            : getEvaluationScoreColor(
                                                averageScore,
                                              )
                                      }`}
                                    >
                                      {completedEvaluations === 0
                                        ? 'Not run'
                                        : averageScore === null
                                          ? 'Not applicable'
                                          : formatEvaluationScore(averageScore)}
                                    </span>
                                  </div>
                                  <Progress
                                    value={
                                      (completedEvaluations / totalEvaluators) *
                                      100
                                    }
                                  />

                                  <div className="mt-2 grid gap-2 lg:grid-cols-2">
                                    {visibleEvaluators.map((evaluator) => {
                                      const result =
                                        simulationEvaluations[evaluator.id];
                                      const pendingResult =
                                        evaluationResults[simulation.id]?.[
                                          evaluator.id
                                        ];

                                      if (!result) {
                                        return (
                                          <Button
                                            key={evaluator.id}
                                            size="sm"
                                            onClick={() =>
                                              runEvaluation(
                                                simulation.id,
                                                evaluator.id,
                                              )
                                            }
                                            variant="outline"
                                            className="group text-sm"
                                            disabled={
                                              isRunningAll ||
                                              pendingResult?.status ===
                                                'loading' ||
                                              isBlocked
                                            }
                                          >
                                            <span className="flex w-full items-center gap-3">
                                              {isRunningAll ||
                                              pendingResult?.status ===
                                                'loading' ? (
                                                <LoadingSpinner strokeColor="black" />
                                              ) : (
                                                <PlayCircle className="h-4 w-4 text-gray-400 group-hover:text-black" />
                                              )}
                                              <span>
                                                Run {evaluator.name} evaluator
                                              </span>
                                            </span>
                                          </Button>
                                        );
                                      }

                                      const isDetailOpen =
                                        expandedEvaluatorId === evaluator.id;

                                      const scoredCount =
                                        result.scored_message_count;
                                      const isScoreStale =
                                        scoredCount != null &&
                                        scoredCount !==
                                          simulation.messages.length;

                                      return (
                                        <button
                                          key={evaluator.id}
                                          type="button"
                                          data-onboarding={
                                            isFocusedSimulation &&
                                            evaluator.id ===
                                              firstCompletedEvaluatorId
                                              ? 'evaluation-score-expand'
                                              : undefined
                                          }
                                          onClick={() =>
                                            toggleEvaluatorDetail(
                                              simulation.id,
                                              evaluator.id,
                                            )
                                          }
                                          aria-expanded={isDetailOpen}
                                          className={`flex w-full items-center justify-between rounded bg-prompt-safecomfantasy p-2 text-left ${
                                            isDetailOpen
                                              ? 'ring-2 ring-prompt-safecomoutrageous-orange ring-offset-2 ring-offset-prompt-safecomwhite'
                                              : 'hover:ring-1 hover:ring-prompt-safecomoutrageous-orange'
                                          }`}
                                        >
                                          <span className="min-w-0 flex-1 truncate text-sm text-prompt-safecomvulcan">
                                            {evaluator.name}
                                          </span>
                                          <span className="flex shrink-0 items-center gap-2">
                                            {isScoreStale && (
                                              <span
                                                title="Scored against an earlier, shorter version of this transcript. Re-run to refresh."
                                                className="flex items-center gap-1 text-xs text-orange-600"
                                              >
                                                <AlertTriangle className="h-3 w-3" />
                                                Outdated
                                              </span>
                                            )}
                                            <span
                                              className={`rounded px-2 text-sm font-medium ${getEvaluationScoreColor(
                                                result?.score ?? 0,
                                              )}`}
                                              title={getEvaluationScoreTitle(
                                                result?.score ?? 0,
                                              )}
                                            >
                                              {formatEvaluationScore(
                                                result?.score ?? 0,
                                              )}
                                            </span>
                                            <ChevronDown
                                              className={`h-4 w-4 shrink-0 text-prompt-safecomabbey transition-transform duration-200 ${
                                                isDetailOpen
                                                  ? '-rotate-180'
                                                  : ''
                                              }`}
                                              aria-hidden
                                            />
                                          </span>
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>

                                {expandedEvaluatorId &&
                                  expandedEvaluator &&
                                  simulationEvaluations[
                                    expandedEvaluatorId
                                  ] && (
                                    <div className="mt-4 space-y-4">
                                      <EvaluationResultCard
                                        evaluator={expandedEvaluator}
                                        existingResult={
                                          simulationEvaluations[
                                            expandedEvaluatorId
                                          ]
                                        }
                                        pendingResult={
                                          evaluationResults[simulation.id]?.[
                                            expandedEvaluatorId
                                          ]
                                        }
                                      />
                                      <EvaluationConversation
                                        title="Conversation"
                                        simulation={simulation}
                                        forceExpanded={isOnboardingActive}
                                      />
                                      {/* No prompt of ours to improve for agents connected from outside. */}
                                      {!isConnectedAgent(simulation.agent) && (
                                        <ImprovementSuggestions
                                          workspaceId={
                                            user?.currentWorkspace?.workspace_id
                                          }
                                          simulationId={simulation.id}
                                          evaluatorId={expandedEvaluatorId}
                                          agentPrompt={
                                            simulation.agent?.prompt ??
                                            undefined
                                          }
                                          conversation={simulation.messages}
                                          evaluation={
                                            simulationEvaluations[
                                              expandedEvaluatorId
                                            ]
                                          }
                                          evaluator={expandedEvaluator}
                                          onSuggestionsRequested={() =>
                                            markMilestoneComplete(
                                              'improvementSuggestionsRequested',
                                            )
                                          }
                                        />
                                      )}
                                      <div
                                        className="rounded-lg border border-[#00000014] bg-prompt-safecomfantasy p-4"
                                        data-onboarding="evaluation-next-steps"
                                      >
                                        <div className="mb-3">
                                          <h4 className="font-semibold text-prompt-safecomvulcan">
                                            Next steps
                                          </h4>
                                          <p className="text-sm text-prompt-safecomabbey">
                                            Adjust the part of the test that
                                            explains the result, then run the
                                            loop again.
                                          </p>
                                        </div>
                                        <div className="flex flex-col gap-2 sm:flex-row">
                                          <Button
                                            variant="outline"
                                            data-onboarding="evaluation-go-to-agent"
                                            className="flex-1 border-[#00000020] bg-prompt-safecomwhite"
                                            asChild
                                          >
                                            <Link
                                              to={`/agent-builder/${simulation.agent_id}`}
                                            >
                                              <Bot className="h-4 w-4" />
                                              Edit agent
                                              <ExternalLink className="h-3 w-3" />
                                            </Link>
                                          </Button>
                                          <Button
                                            variant="outline"
                                            className="flex-1 border-[#00000020] bg-prompt-safecomwhite"
                                            asChild
                                          >
                                            <Link
                                              to={`/persona-builder/${simulation.persona_id}`}
                                            >
                                              <Users className="h-4 w-4" />
                                              Review persona
                                              <ExternalLink className="h-3 w-3" />
                                            </Link>
                                          </Button>
                                          <Button
                                            variant="outline"
                                            className="flex-1 border-[#00000020] bg-prompt-safecomwhite"
                                            asChild
                                          >
                                            <Link
                                              to={`/evaluator-builder/${expandedEvaluatorId}`}
                                            >
                                              <ClipboardList className="h-4 w-4" />
                                              Refine evaluator
                                              <ExternalLink className="h-3 w-3" />
                                            </Link>
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                              </div>
                            );
                          })}
                        </TabsContent>
                      ),
                    )}
                  </Tabs>
                ) : (
                  <div className="py-8 text-center text-prompt-safecomabbey">
                    No simulations available. Start a conversation simulation to
                    generate evaluations.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};
